/** Register wizard constants (WP-RD-10). Copy lives in the i18n `auth.register` namespace. */
import type { IndustryKey } from "@sagot/constants";

/**
 * Wizard order (D9 of the redesign plan): the mockup verifies the e-mail before the account exists, the API creates the account
 * first. The panes are the mockup's; the code pane moved behind the consents because it needs the account.
 */
export const REGISTER_STEPS = ["email", "about", "organization", "consents", "verify", "ready"] as const;
export type RegisterStep = (typeof REGISTER_STEPS)[number];

/** "Step n of 5": the five numbered panes; the ready pane is the end (progress 100 %, no label). */
export const REGISTER_NUMBERED_STEPS = 5;

export const REGISTER_PARAMS = { step: "step", email: "email", plan: "plan", next: "next" } as const;

/** sessionStorage keys (this tab only). The draft never holds the password, the consents or the security-check token. */
export const REGISTER_DRAFT_KEY = "sagot:register-draft";
export const SIGNUP_PLAN_KEY = "sagot:signup-plan";

/** Plans the pricing page can send (`/register?plan=`). Anything else is ignored. */
export const REGISTER_PLAN_NAMES = { starter: "Starter", standard: "Standard", pro: "Pro" } as const;
export type RegisterPlanKey = keyof typeof REGISTER_PLAN_NAMES;

/** Mockup: the code is checked automatically 350 ms after the sixth digit. */
export const OTP_AUTO_SUBMIT_MS = 350;
/** Mockup: the field of a new pane takes focus after 80 ms (the pane is sliding in). */
export const PANE_FOCUS_DELAY_MS = 80;
/** Mockup: a name needs more than one character. */
export const NAME_MIN_LENGTH = 2;

/** Server field names of `POST /v1/auth/register` -> the pane that owns them (422 `fields`). */
export const FIELD_STEP: Readonly<Record<string, RegisterStep>> = {
  email: "email",
  name: "about",
  password: "about",
  org_name: "organization",
  org_type: "organization",
  size: "organization",
  country: "organization",
  "consents.terms": "consents",
  "consents.dpa": "consents",
  consents: "consents",
  captcha_token: "consents",
};

/** API error codes that send the visitor back to the password. */
export const PASSWORD_ERROR_CODES: readonly string[] = ["weak_password", "breached_password"];

export const DEFAULT_COUNTRY = "PH";

/** The blurred console behind the card (decorative, `aria-hidden`; the mockup's `.r-console`). */
export const CONSOLE_BACKDROP_LABEL = "Sagot Console";

export const BACKDROP_NAV = [
  { label: "Inbox", count: 12, active: true },
  { label: "Knowledge" },
  { label: "Leads", count: 6 },
  { label: "Knowledge gaps" },
  { label: "Analytics" },
  { label: "Widget Studio" },
  { label: "Channels" },
  { label: "Settings" },
] as const;

export const BACKDROP_THREADS = [
  { who: "Parent · Website", text: "Magkano po ang tuition sa Grade 11?" },
  { who: "Guest · Mobile app", text: "Pwede bang magdala ng aso?" },
  { who: "Jun Mercado · Web app", text: "Interested in Timog Park" },
  { who: "Shopper · Messenger", text: "May stock pa po ba?" },
  { who: "Patient · Website", text: "How much is a tooth extraction?" },
  { who: "Applicant · Website", text: "Kailan po ang deadline?" },
] as const;

export const BACKDROP_MESSAGES = [
  { mine: true, text: "Magkano po ang tuition sa Grade 11?" },
  { mine: false, text: "Grade 11 tuition for SY 2027–2028 is ₱52,000 per year, payable in full or in 10 monthly installments." },
  { mine: true, text: "Pwede po ba mag-campus tour this Saturday?" },
  { mine: false, text: "Yes po! Tours run Saturdays at 9:00 AM and 1:00 PM." },
] as const;

/** Industry chips in the mockup's order (the nine backend industries). */
export const REGISTER_INDUSTRY_ORDER: readonly IndustryKey[] = [
  "education",
  "healthcare",
  "real_estate",
  "hospitality",
  "human_resources",
  "government",
  "ecommerce",
  "business",
  "other",
];
